import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { staggerItem } from "@/components/motion/Reveal";
import SmartImg from "@/components/SmartImg";

export default function ProductCard({ product, accent = "#DA3C30", dark = false }) {
  const { t, loc } = useLang();

  const cardCls = dark
    ? "bg-clara-surface border-clara-text/10 hover:border-clara-accent"
    : "bg-white border-line hover:border-ink";
  const imgBg = dark ? "bg-clara-bg" : "bg-cream";
  const title = dark ? "text-clara-text" : "text-ink";
  const body = dark ? "text-clara-text/70" : "text-inkmute";
  const label = dark ? "text-clara-text/50" : "text-inkmute";
  const strong = dark ? "text-clara-text" : "text-ink";
  const border = dark ? "border-clara-text/15" : "border-line";
  const borderSoft = dark ? "border-clara-text/10" : "border-line/60";
  const indBadge = dark ? "bg-clara-text/10 text-clara-text/80" : "bg-ink/5 text-ink";

  return (
    <motion.article
      variants={staggerItem}
      data-testid={`product-card-${product.slug}`}
      className={`group border transition-colors duration-300 flex flex-col ${cardCls}`}
    >
      <div className={`relative overflow-hidden aspect-[4/5] ${imgBg}`}>
        <SmartImg
          src={product.image_url}
          alt={loc(product.name)}
          className="h-full w-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <span className="absolute top-4 left-4 h-1 w-10 group-hover:w-16 transition-all duration-300" style={{ background: accent }} />
      </div>

      <div className="p-6 md:p-7 flex flex-col flex-1">
        <h3 className={`font-serif text-2xl leading-tight ${title}`}>{loc(product.name)}</h3>
        <p className={`mt-3 text-sm leading-relaxed flex-1 ${body}`}>{loc(product.description)}</p>

        <div className="mt-5">
          <div className={`text-[10px] uppercase tracking-widest font-semibold ${label}`}>{t("common.application")}</div>
          <p className={`text-sm mt-1 leading-relaxed ${strong}`}>{loc(product.application)}</p>
        </div>

        {/* Packaging table */}
        <div className={`mt-5 border-t pt-4 ${border}`}>
          <div className={`text-[10px] uppercase tracking-widest font-semibold mb-2 ${label}`}>{t("common.packaging")}</div>
          <table className="w-full text-sm tabular-nums">
            <tbody>
              {product.packaging.map((p, i) => (
                <tr key={i} className={`border-b last:border-0 ${borderSoft}`}>
                  <td className={`py-1.5 pr-2 font-semibold ${strong}`}>{p.size}</td>
                  <td className={`py-1.5 pr-2 ${body}`}>{loc(p.type)}</td>
                  <td className="py-1.5 text-right">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${p.segment === "retail" ? "bg-claris-green/15 text-claris-green" : indBadge}`}>
                      {p.segment === "retail" ? t("common.retail") : t("common.industrial")}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-4 text-xs">
          <div>
            <div className={`text-[10px] uppercase tracking-widest font-semibold ${label}`}>{t("common.shelfLife")}</div>
            <div className={`mt-1 ${strong}`}>{loc(product.shelf_life)}</div>
          </div>
          <div>
            <div className={`text-[10px] uppercase tracking-widest font-semibold ${label}`}>{t("common.storage")}</div>
            <div className={`mt-1 ${strong}`}>{loc(product.storage)}</div>
          </div>
        </div>
      </div>
    </motion.article>
  );
}
